import Vue from 'vue';
import VueRouter from 'vue-router';
import { setTitlePrefix } from '@/infrastructure/utils/setTitle';
import _ from 'lodash';
import { loadWithVxeTable } from '@/infrastructure/utils/load-vxetable';
import { mapAllPlugins } from '@/presentation/pages/detail/basic/interface_lock/DetailPluginLock';
import { injectService } from './inversify.config';
import { SpaceRouteName } from './domains/spaces/enums';
import { HeaderServiceManager } from './domains/header/header-service';
const TAB_DEFAULT = 'folder';
export const getTabParams = params => {
    const { tab = TAB_DEFAULT } = params;
    return { tab };
};
const getDetailPageProps = location => {
    const { query } = location;
    return getTabParams(query);
};
function enableTitleChanger(to) {
    const headerServiceManager = injectService(HeaderServiceManager);
    const headerService = headerServiceManager.getHeaderServiceInstance();
    const isWhiteLabel = headerService.whiteLabel;
    const { path, name } = to;
    if (path.indexOf('/task') === 0 && name !== 'taskCenter') {
        setTitlePrefix({ prefix: '', type: 'taskDetail', isWhiteLabel });
    }
    else {
        // 重置
        setTitlePrefix({ prefix: '', isWhiteLabel });
    }
}
function registerDetailPlugins() {
    const detailPlugins = mapAllPlugins();
    detailPlugins.forEach(PluginClass => {
        new PluginClass().register();
    });
}
export const router = new VueRouter({
    routes: [],
    mode: localStorage._useHash ? 'hash' : 'history',
});
export function addRoutes(router) {
    import('@/presentation/pages/detail/DetailRoot.vue');
    import('@/presentation/pages/SearchResult/PatentSearchResult.vue');
    Vue.use(VueRouter);
    const routes = [
        {
            path: '/',
            component: () => import('@/presentation/pages/home/Home.vue'),
            children: [
                {
                    path: 'my',
                    name: 'my',
                    redirect(to) {
                        const spaceType = to.query.spaceType;
                        if (spaceType === 'self') {
                            return { name: SpaceRouteName.Personal, query: {} };
                        }
                        if (spaceType === 'shared') {
                            return { name: SpaceRouteName.Shared, query: {} };
                        }
                        if (spaceType === 'public') {
                            return { name: SpaceRouteName.Public, query: {} };
                        }
                        return { name: 'home' };
                    },
                },
                {
                    path: 'recycle',
                    name: 'recycle',
                    component: () => import('@/presentation/pages/WorkspaceRecycle.vue'),
                },
                {
                    path: 'taskCenter',
                    name: 'taskCenter',
                    component: () => import('@/presentation/pages/WorkspaceTaskCenter.vue'),
                },
                {
                    path: 'assigned',
                    name: 'assigned',
                    component: () => import('@/presentation/pages/assigned/Assigned.vue'),
                },
                {
                    path: 'myComments',
                    name: 'myComments',
                    component: () => import('@/presentation/pages/WorkspaceMyComments.vue'),
                },
                {
                    path: 'customField',
                    name: 'customField',
                    component: () => import('@/presentation/pages/custom-field/CustomField.vue'),
                },
                {
                    path: 'resource-import-result',
                    name: 'resourceImportResult',
                    component: () => loadWithVxeTable(() => import('@/presentation/pages/ResourceImport/ResourceImportTaskResult.vue')),
                },
                {
                    path: 'patentSearchResult',
                    name: 'patentSearchResult',
                    component: () => import('@/presentation/pages/SearchResult/PatentSearchResult.vue'),
                },
                {
                    path: '/customField',
                    name: 'customField',
                    component: () => import('@/presentation/pages/custom-field/CustomField.vue'),
                },
                {
                    path: '/assigned',
                    name: 'assigned',
                    component: () => import('@/presentation/pages/assigned/Assigned.vue'),
                },
                {
                    path: '/onboarding',
                    name: 'onboarding',
                    component: () => import('@/presentation/pages/home/onboarding/Onboarding.vue'),
                },
                {
                    path: '/home',
                    name: 'home',
                    component: () => import('@/presentation/pages/home/WelcomePage.vue'),
                },
                {
                    path: '/personal-workspaces',
                    name: SpaceRouteName.Personal,
                    component: () => import('@/presentation/pages/home/workspaces/TypeWorkspaces.vue'),
                },
                {
                    path: '/shared-workspaces',
                    name: SpaceRouteName.Shared,
                    component: () => import('@/presentation/pages/home/workspaces/TypeWorkspaces.vue'),
                },
                {
                    path: '/public-workspaces',
                    name: SpaceRouteName.Public,
                    component: () => import('@/presentation/pages/home/workspaces/TypeWorkspaces.vue'),
                },
                {
                    path: '',
                    redirect: '/my',
                },
            ],
        },
        {
            path: '/detail',
            name: 'detail',
            component: () => import('@/presentation/pages/workspace-detail/DetailRoot.vue'),
            children: [
                {
                    // 兼容老路由, 比如导航库跳转过来只指定了 dataType
                    path: ':dataType',
                    redirect: (to) => {
                        return { name: 'detailContent', params: { ...to.params, viewType: 'default' }, query: to.query };
                    },
                },
                {
                    path: ':dataType/:viewType',
                    name: 'detailContent',
                    component: () => import('@/presentation/pages/workspace-detail/components/content/Content.vue'),
                },
            ],
        },
        {
            path: '/settings',
            name: 'settings',
            component: () => import('@/presentation/pages/settings/WorkspaceSettings.vue'),
            props: getDetailPageProps,
            redirect: { name: 'fields' },
            children: [{
                    path: 'fields',
                    name: 'fields',
                    component: () => import('@/presentation/pages/settings/FieldsSettings.vue'),
                }, {
                    path: 'folder',
                    name: 'folder',
                    component: () => import('@/presentation/pages/settings/FolderSettings.vue'),
                }, {
                    path: 'fieldDisplayTemplate',
                    name: 'fieldDisplayTemplate',
                    component: () => import('@/presentation/pages/settings/FieldDisplayTemplateSettings.vue'),
                }, {
                    path: 'overall',
                    name: 'overall',
                    component: () => import('@/presentation/pages/settings/OverallSettings.vue'),
                }, {
                    path: 'operationRecords',
                    name: 'operationRecords',
                    component: () => import('@/presentation/pages/settings/operation-records/OperationRecords.vue'),
                }, {
                    path: 'collaboration',
                    name: 'collaboration',
                    component: () => import('@/presentation/pages/settings/CollaborationSettings.vue'),
                }],
        },
        {
            path: '/resource-import',
            name: 'resourceImport',
            component: () => loadWithVxeTable(() => import('@/presentation/pages/ResourceImport/ResourceImportMain.vue')),
            props: getDetailPageProps,
        },
        {
            path: '/task',
            component: () => import('@/presentation/pages/TaskDetail.vue'),
            children: [
                {
                    path: '',
                    name: 'taskMainPart',
                    component: () => import('@/presentation/components/task/detail/TaskMainPart.vue'),
                },
            ],
        },
        {
            path: '/homeImport',
            name: 'homeImport',
            component: () => loadWithVxeTable(() => import('@/presentation/pages/HomeImport/HomeImport.vue')),
        },
        {
            path: '/aiImport',
            name: 'aiImport',
            component: () => import('@/presentation/pages/ai-import/AiImport.vue'),
            redirect: { name: 'importTarget' },
            children: [{
                    path: 'importTarget',
                    name: 'importTarget',
                    component: () => import('@/presentation/pages/ai-import/ImportTarget.vue'),
                }, {
                    path: 'importMode',
                    name: 'importMode',
                    component: () => import('@/presentation/pages/ai-import/ImportMode.vue'),
                }, {
                    path: 'importExcel',
                    name: 'importExcel',
                    component: () => loadWithVxeTable(() => import('@/presentation/pages/ai-import/ImportExcel.vue')),
                    redirect: { name: 'importSteps' },
                    children: [{
                            path: 'importSteps',
                            name: 'importSteps',
                            component: () => import('@/presentation/pages/ai-import/ImportSteps.vue'),
                            redirect: { name: 'upload' },
                            children: [{
                                    path: 'upload',
                                    name: 'upload',
                                    component: () => import('@/presentation/pages/ai-import/components/UploadFile.vue'),
                                }, {
                                    path: 'preview',
                                    name: 'preview',
                                    component: () => import('@/presentation/pages/ai-import/components/ImportPreview.vue'),
                                }, {
                                    path: 'import',
                                    name: 'import',
                                    component: () => import('@/presentation/pages/ai-import/components/ImportProcess.vue'),
                                }],
                        }, {
                            path: 'importResult',
                            name: 'importResult',
                            component: () => import('@/presentation/pages/ai-import/ImportResult.vue'),
                        }],
                }, {
                    path: 'importPN',
                    name: 'importPN',
                    component: () => import('@/presentation/pages/ai-import/ImportPN.vue'),
                }],
        },
        {
            path: '/share/fromShareLink',
            name: 'fromShareLink',
            component: () => import('@/presentation/pages/share/FromShareLink.vue'),
        },
        {
            path: '/access/noPermission',
            name: 'accessNoPermission',
            component: () => import('@/presentation/pages/access/NoPermission.vue'),
        },
        {
            path: '/403',
            name: 'page-not-authorized',
            component: () => import('@/presentation/pages/request-demo/403.vue'),
        },
        {
            path: '/patent-consult',
            name: 'patentConsult',
            component: () => import('@/plugins/patent-consult/PatentConsultView.vue'),
            children: [
                {
                    path: '/patent-consult/:id',
                    name: 'patentConsultDetail',
                    component: () => import('@/plugins/patent-consult/PatentConsultDetail.vue'),
                },
            ],
        },
        {
            path: '*', // 此处最底部
            redirect: '/home',
        },
    ];
    router.addRoutes(routes);
    const originalPush = VueRouter.prototype.push;
    VueRouter.prototype.push = function push(location) {
        return originalPush.call(this, location).catch(err => console.warn(err));
    };
    registerDetailPlugins();
    router.afterEach((to /* from */) => {
        _.set(window, 'zPREFACE.trackData.source', to.name);
        enableTitleChanger(to);
    });
}
export function getLeafRoutes(routeConf) {
    function getLeafRoutesRecursively(routeConf) {
        if (!routeConf.children || routeConf.children.length === 0) {
            return [routeConf];
        }
        return routeConf.children.reduce((acc, cur) => {
            return acc.concat(getLeafRoutesRecursively(cur));
        }, []);
    }
    return getLeafRoutesRecursively(routeConf);
}
let stackPromise = Promise.resolve();
export function stackReplace(router, config) {
    const getStackReplaceConfig = () => {
        if (typeof config === 'function') {
            return config(router);
        }
        return config;
    };
    stackPromise = stackPromise
        .then(() => {
        return router.replace(getStackReplaceConfig());
    })
        .catch((error) => {
        console.warn('[stackReplace Error]: ', error);
        return router.replace(getStackReplaceConfig());
    });
    return stackPromise;
}
// 左右需要强刷的需求
export function refreshWithParamsAsQuery(params = {}, query = {}) {
    const newQuery = _.assign({ _: _.now() }, query);
    return router.push({
        name: router.currentRoute.name,
        query: _.merge({}, router.currentRoute.query, params, newQuery),
    });
}
